// ===========================================
// HOOK: FIELD SANITIZATION
// Sanitiza campos según configuración schema
// Cumplimiento SAT México
// ===========================================
import { useCallback } from 'react';

const useFieldSanitization = () => {
  
  /**
   * Limpia texto para cumplimiento SAT
   * Elimina: saltos de línea, tabs, emojis, caracteres especiales
   */
  const sanitizeForSAT = useCallback((text) => {
    if (!text || typeof text !== 'string') return text;
    
    return text
      // 1. Eliminar saltos de línea y retornos de carro
      .replace(/[\r\n]+/g, ' ')
      // 2. Eliminar tabuladores
      .replace(/[\t]+/g, ' ')
      // 3. Eliminar emojis y símbolos
      .replace(/[^\x00-\x7F\xA0-\xFF\u0100-\u017F\u0180-\u024F]/g, '')
      // 4. 🔥 Caracteres NO permitidos SAT
      .replace(/[!|<>\\:áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙ]/g, '')
      // 5. Normalizar espacios múltiples
      .replace(/\s+/g, ' ')
      // 6. Trim
      .trim();
  }, []);

  /**
   * Limpia texto genérico (menos restrictivo)
   * Solo elimina saltos de línea y espacios múltiples
   */
  const sanitizeBasic = useCallback((text) => {
    if (!text || typeof text !== 'string') return text;
    
    return text
      .replace(/[\r\n]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }, []);

  /**
   * Sanitiza campo según configuración del schema
   */
  const sanitizeField = useCallback((value, fieldConfig = {}) => {
    if (!value || typeof value !== 'string') return value;
    
    const { sanitize } = fieldConfig;
    
    // Sin configuración, retornar original
    if (!sanitize) return value;
    
    // Aplicar nivel de sanitización
    switch (sanitize) {
      case 'sat':
        return sanitizeForSAT(value);
      case 'basic':
        return sanitizeBasic(value);
      case 'none':
        return value;
      default:
        return sanitizeBasic(value);
    }
  }, [sanitizeForSAT, sanitizeBasic]);

  /**
   * Sanitiza MIENTRAS el usuario escribe (menos agresivo)
   * NO hace trim para no interferir con la escritura
   */
  const sanitizeOnType = useCallback((text, level = 'basic') => {
    if (!text || typeof text !== 'string') return text;
    
    let result = text;
    
    if (level === 'sat') {
      result = text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/[^\x00-\x7F\xA0-\xFF\u0100-\u017F\u0180-\u024F]/g, '')
        .replace(/[!|<>\\:áéíóúÁÉÍÓÚàèìòùÀÈÌÒÙ]/g, '') // 🔥
        .replace(/\s+/g, ' ');
    } else if (level === 'basic') {
      result = text
        .replace(/[\r\n\t]+/g, ' ')
        .replace(/\s+/g, ' ');
    }
    
    return result;
  }, []);

  /**
   * Sanitiza al SALIR del campo (completo con trim)
   */
  const sanitizeOnBlur = useCallback((text, level = 'basic') => {
    if (!text || typeof text !== 'string') return text;
    
    if (level === 'sat') {
      return sanitizeForSAT(text);  // Con trim
    } else if (level === 'basic') {
      return sanitizeBasic(text);  // Con trim
    }
    
    return text;
  }, [sanitizeForSAT, sanitizeBasic]);

  return {
    sanitizeField,
    sanitizeForSAT,
    sanitizeBasic,
    sanitizeOnType,
    sanitizeOnBlur
  };
};

export default useFieldSanitization;